import { useEffect, useRef } from "react";

/**
 * Leave the success screen on a timer.
 *
 * Every trade flow confirms with "Bought/Sold X USDv" and then has nothing
 * further to say, but nothing moved the user off that receipt. `BuyWidget`
 * locks its CTA (`showCta={false}`, `ctaLabel` equal to the success label,
 * no `onClick`), and the landing surface that renders it has no Back link
 * by design — so success was terminal and a refresh was the only way out.
 *
 * Fires `onReturn` once, `delay` ms after `isSuccess` goes true, and clears
 * the timer if the flow leaves success first. The callback decides where
 * the user lands: the widget resets its own form state, the page routes.
 *
 * Sibling of `useAutoDismissUserCancel`, which does the same job for the
 * wallet-reject path. Callers gate on sim mode themselves — scripted demos
 * park on the receipt on purpose.
 *
 * @param isSuccess True while the flow is in its success phase.
 * @param onReturn  Runs once per success window.
 * @param delay     Milliseconds to hold the receipt. Default 3000ms.
 */
export function useSuccessAutoReturn(isSuccess: boolean, onReturn: () => void, delay = 3000): void {
    // Hold the callback in a ref so a parent re-render with a fresh closure
    // does not re-arm the timer. Without this an unmemoised handler restarts
    // the countdown on every render and the receipt never clears.
    const onReturnRef = useRef(onReturn);
    onReturnRef.current = onReturn;

    useEffect(() => {
        if (!isSuccess) return;
        const t = window.setTimeout(() => onReturnRef.current(), delay);
        return (): void => window.clearTimeout(t);
    }, [isSuccess, delay]);
}
